import React, { useEffect } from "react";
import { Divider } from "@UI/Components/Divider/Divider";
import { Stack, HStack, VStack } from "@UI/Components/Stack/Stack";
import { Icon } from "@UI/Components/Icon/Icon";
import { AngleDown } from "@UI/Components/Icons/AngleDown";
import { Heading } from "@UI/Components/Heading/Heading";
import { Text } from "@UI/Components/Text/Text";
import { Button } from "@UI/Components/Button/Button";
import { Xmark } from "@UI/Components/Icons/Xmark";
import { useServiceRequestCandidates } from "../../hooks/useCandidates";
import { CandidateFilters, ICandidate, ICandidateFilter } from "../../types";
import { CandidateFilterModal } from "./CandidateFilterModal";
import { useDisclosure } from "@UI/hooks/useDisclosure";
import { useParams } from "@Libs/Router/hooks/useParams";
import { serviceRequestPathFragment } from "../../config/routes";
import { Spinner } from "@UI/Components/Spinner/Spinner";
import { ServiceCategorySlider } from "../ServiceCategorySlider";
import { EmptyCandidatesPlaceholder } from "./EmptyCandidatesPlaceholder";
import { DeclineOfferModal } from "./DeclineOffer/DeclineOfferModal";

import { AlertBox } from "@UI/Components/AlertBox/AlertBox";
import { useToast, ToastProps } from "@UI/hooks/useToast";
import { v4 as uuidv4 } from "uuid";
import useMediaQuery from "@UI/hooks/useMediaQuery";
import { CandidateOfferModal } from "../ServiceOfferModal";
import { CandidateCard } from "./CandidateCard";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import messages from "../messages";
import { useServiceRequestFlow } from "@modules/ServiceRequest/hooks/useServiceRequestFlow";

export const CandidatesSection = () => {
  const { serviceRequest } = useServiceRequestFlow();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const {
    isOpen: isDeclineOfferModalOpen,
    onOpen: onDeclineOfferModalOpen,
    onClose: onDeclineOfferModalClose,
  } = useDisclosure();

  const {
    isOpen: isCandidateOfferModalOpen,
    onOpen: onCandidateOfferModalOpen,
    onClose: onCandidateOfferModalClose,
  } = useDisclosure();

  const toast = useToast();
  const { isMobile } = useMediaQuery();

  const [filter, setFilter] = React.useState<ICandidateFilter>(
    CandidateFilters.recommended
  );

  const [selectedCandidate, setSelectedCandidate] =
    React.useState<ICandidate | null>(null);

  const { id: serviceRequestId } = useParams([
    serviceRequestPathFragment.SERVICE_REQUEST,
  ]);

  const {
    candidates = [],
    refetch,
    isLoading,
    removeCandidateFromList,
    removeUnreadIndicatorFromConversation,
  } = useServiceRequestCandidates({
    serviceRequestId,
    sortBy: filter,
  });

  useEffect(() => {
    serviceRequest.appliedServiceProvidersCount > 0 && refetch();
  }, [filter]);

  const handleFilterChange = (value: ICandidateFilter) => {
    setFilter(value);
    onClose();
  };

  const handleCandidateSelection = (
    conversationId: ICandidate["conversationId"]
  ) => {
    setSelectedCandidate(() =>
      candidates.find((c) => c.conversationId === conversationId)
    );
  };

  const handleDeclineOfferModalOpen = (
    conversationId: ICandidate["conversationId"]
  ) => {
    handleCandidateSelection(conversationId);
    onDeclineOfferModalOpen();
  };
  const handleCandidateOfferModalOpen = (
    conversationId: ICandidate["conversationId"]
  ) => {
    handleCandidateSelection(conversationId);
    onCandidateOfferModalOpen();
  };

  const showToast = (toastData: Pick<ToastProps, "status" | "description">) => {
    const { status, description } = toastData;
    const toastId = uuidv4();
    toast({
      id: toastId,
      status,
      position: isMobile ? "bottom" : "top-right",
      duration: 2000,
      isClosable: true,
      render: () => (
        <AlertBox
          status={status}
          background="white"
          childrenContainerStyle={{ justifyContent: "start" }}
          marginTop={!isMobile ? "80px" : "initial"}
        >
          <HStack justifyContent="space-between" flexGrow={1}>
            <Text m="0" ml={3} color="gray.700">
              {description}
            </Text>
            <Icon
              as={Xmark}
              color="gray.700"
              cursor="pointer"
              onClick={() => toast.close(toastId)}
            />
          </HStack>
        </AlertBox>
      ),
    });
  };

  return (
    <VStack w="full" spacing="0" alignItems="start" flexGrow={1}>
      <Stack
        mt="32px"
        mb="20px"
        w="full"
        flexDir={{ base: "column", md: "row" }}
        alignItems={{ base: "start", md: "center" }}
        justifyContent="space-between"
        spacing={{ base: "8px", lg: "0" }}
      >
        <Heading as="h4" fontSize={{ base: "1.5rem", md: "1.75rem" }}>
          <FormattedMessage {...messages["serviceRequest.candidates.header"]} />{" "}
          ({serviceRequest.appliedServiceProvidersCount})
        </Heading>

        {candidates?.length > 0 && (
          <HStack
            w={{ base: "full", md: "auto" }}
            spacing="8px"
            justifyContent="center"
            ml="5px"
          >
            <Text fontSize={{ base: "0.875rem", md: "initial" }} m="0">
              <FormattedMessage {...messages["filterBy"]} />:
            </Text>

            <Button
              variant="outline"
              bg="white"
              borderColor="gray.400"
              color="gray.700"
              _hover={{ bg: "white", borderColor: "gray.500" }}
              flexGrow={{ base: 1, md: 0 }}
              justifyContent={"space-between"}
              rightIcon={<Icon as={AngleDown} width="16px" height="16px" />}
              onClick={onOpen}
            >
              <FormattedMessage
                {...messages[
                  filter === CandidateFilters.last_updated
                    ? "lastUpdated"
                    : "recommended"
                ]}
              />
            </Button>
          </HStack>
        )}
      </Stack>

      <CandidateFilterModal
        isOpen={isOpen}
        onClose={onClose}
        filter={filter}
        onFilterChange={handleFilterChange}
      />

      {serviceRequest.appliedServiceProvidersCount < 1 && !isLoading && (
        <>
          <EmptyCandidatesPlaceholder />
          <Divider
            color="sand.300"
            my={{ base: "20px !important", lg: "32px !important" }}
          />
          <ServiceCategorySlider />
        </>
      )}

      {isLoading && (
        <VStack w="full" justifyContent="center" py="50px" px="16px">
          <Spinner color="gray.500" size="lg" />
        </VStack>
      )}

      {Array.isArray(candidates) && candidates.length > 0 && (
        <VStack w="full" alignItems="start">
          {Array.isArray(candidates) &&
            candidates.length > 0 &&
            candidates.map((item, index) => (
              <CandidateCard
                onCandidateOfferModalOpen={handleCandidateOfferModalOpen}
                removeUnreadIndicatorFromConversation={
                  removeUnreadIndicatorFromConversation
                }
                onDeclineOfferModalOpen={handleDeclineOfferModalOpen}
                key={`${item?.id}-${index}`}
                {...item}
              />
            ))}
        </VStack>
      )}

      <CandidateOfferModal
        candidate={selectedCandidate}
        isOpen={isCandidateOfferModalOpen}
        onClose={onCandidateOfferModalClose}
        onDeclineOfferModalOpen={handleDeclineOfferModalOpen}
      />

      <DeclineOfferModal
        isOpen={isDeclineOfferModalOpen}
        onClose={onDeclineOfferModalClose}
        conversationId={selectedCandidate?.conversationId}
        serviceProviderName={selectedCandidate?.firstName || ""}
        isOfferMade={!!selectedCandidate?.serviceOffer}
        showToast={showToast}
        removeCandidateFromList={removeCandidateFromList}
      />
    </VStack>
  );
};
